import React, { useState, useContext } from 'react';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';
import { domainForSegment } from 'common';
import { ANALYTICS_MESSAGES, SCREEN_NAMES } from 'common/utils/constants/AnalyticsConstants';
import { getAnalyticsBaseConfig, getErrorTypeByCode } from '../../common/utils/Common';
import {
  Button,
  Loader,
  ModalOverlay,
  Headings,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { FormTitles, FormTitleTranslationKeys, getDecryptedValue, UserDetails } from 'common';
import TextNotificationWrapper from '../../templates/profile/TextNotificationWrapper';
import PaperlessCommunications from '../../templates/profile/PaperlessCommunications';
import { SmsConsent } from '../../templates/profile/interface';
import { GlobalContext } from '../../context';
import { usePreferenceUpdate } from '@sentara-health/sentara-api-hooks-core';
import { updatePreferenceHelper } from '../../templates/profile/updatePreferenceHelper';

interface CommunicationPreferencesProps {
  smsConsent?: SmsConsent;
  formData: any;
  page: number;
  setPage: (response: any) => void;
}

const CommunicationPreferences: React.FC<CommunicationPreferencesProps> = ({
  formData,
  page,
  setPage,
}) => {
  const { profileState, setProfileState, getFeature } =
    useContext(GlobalContext);
  const { logging } = useContext(GlobalContext);
  const { emailID } = formData[FormTitles.indexOf('Account Setup')];
  const [success, setSuccess] = useState(false);
  const [isEmailModal, setIsEmailModal] = useState(false);
  const [isText, setIsText] = useState(false);
  const [isTextModal, setIsTextModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updatePreference } = usePreferenceUpdate();
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [emailTermsChecked, setEmailTermsChecked] = useState(false);
  const [textOptIn, setTextOptIn] = useState(false);
  const [textTermsChecked, setTextTermsChecked] = useState(false);
  const [textHasPhoneNumber, setTextHasPhoneNumber] = useState(false);
  const [textPhoneNumber, setTextPhoneNumber] = useState<string>('');
  const [textPhoneType, setTextPhoneType] = useState<string>('mobile');

  // Adobe analytics hooks
  const { formSubmissionSucceeded, formSubmissionFailed } = useAdobeAnalytics();
  const { pathname } = useLocation();

  const handleSuccess = (email: string) => {
    setSuccess(true);
    console.log('handleSuccess called with email:', email);
  };

  const onSubmit = async () => {
    setLoading(true);

    const registerId = await getDecryptedValue(UserDetails.registerId);

    try {
      // Determine preference option
      if (registerId === null || registerId === undefined) {
        return;
      }

      let preferenceOption = 'None';

      if (textOptIn && textTermsChecked && textHasPhoneNumber) {
        preferenceOption = 'text';
      }

      const textPayload = updatePreferenceHelper({
        loginMemberId: registerId,
        telecom: textPhoneNumber
          ? [
            {
              system: 'phone',
              value: textPhoneNumber,
              use: textPhoneType.toLocaleLowerCase(),
            },
          ]
          : undefined,
        telecomStatus:
          textOptIn && textTermsChecked && textHasPhoneNumber
            ? 'Opt-in'
            : 'Opt-out',
        termsAndCondition: textTermsChecked,
        preferences: {
          option: preferenceOption,
        },
      });

      const textResult = await updatePreference(textPayload);

      if (
        emailOptIn &&
        emailTermsChecked &&
        textOptIn &&
        textTermsChecked &&
        textHasPhoneNumber
      ) {
        preferenceOption = 'email,text';
      } else if (emailOptIn && emailTermsChecked) {
        preferenceOption = 'email';
      }

      // Single combined API call with all consents
      const emailPayload = updatePreferenceHelper({
        loginMemberId: registerId,
        status: emailOptIn && emailTermsChecked ? 'Opt-in' : 'Opt-out',
        preferences: {
          option: preferenceOption,
        },
      });

      const emailResult = await updatePreference(emailPayload);

      // Adobe analytics logging based on API response
      if (textResult?.error || emailResult?.error) {
        // AppInsights error with apiDetails
        const apiDetails = {
          exception: {
            errorType: getErrorTypeByCode(textResult?.errorCode?.toString()),
            message: textResult?.errorDetails ?? emailResult?.errorDetails,
            stack: textResult?.error ?? emailResult?.error,
          },
          response: {
            httpStatus: textResult?.errorCode ?? emailResult?.errorCode,
          },
        };
        logging?.error({
          additionalDetails: getAnalyticsBaseConfig({ userId: '' }),
          apiDetails,
          message: ANALYTICS_MESSAGES.REG_COMMUNICATIONPREF_EXCEPTION,
          screenName: SCREEN_NAMES.REGISTRATION,
        });
      } else {
        // AppInsights success trace
        logging?.trace({
          additionalDetails: getAnalyticsBaseConfig(),
          message: ANALYTICS_MESSAGES.REG_COMMUNICATIONPREF_SUCCESS,
          screenName: SCREEN_NAMES.REGISTRATION,
        });
      }
      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['communicationPreferencesSuccess'],
      });
      setPage((currPage: number) => currPage + 1);

      // Success
      setSuccess(true);
      setLoading(false);
    } catch (error) {
      // AppInsights error logging
      logging?.error({
        additionalDetails: getAnalyticsBaseConfig({ userId: '' }),
        message: ANALYTICS_MESSAGES.REG_COMMUNICATIONPREF_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
      });
      console.error('Error updating preferences:', error);
      setLoading(false);
    }
  };

  const cancelButton = () => {
    // Adobe analytics cancel logging
    formSubmissionFailed({
      segment: `${domainForSegment}${pathname}`,
      form: AdobeFormConfig['communicationPreferencesCancel'],
    });
    // AppInsights cancel logging
    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig(),
      message: ANALYTICS_MESSAGES.REG_COMMUNICATIONPREF_CANCEL,
      screenName: SCREEN_NAMES.REGISTRATION,
    });
    setPage((currPage: number) => currPage + 1);
    setIsEmailModal(false);
    setIsTextModal(false);
  };

  // Calculate if continue button should be disabled
  const isContinueDisabled = () => {
    // If email opted in but terms not checked, disable
    if (emailOptIn && !emailTermsChecked) {
      return true;
    }
    // If text opted in but terms not checked OR no phone number, disable
    if (textOptIn && (!textTermsChecked || !textHasPhoneNumber)) {
      return true;
    }
    // If both opted in, at least one terms checkbox must be checked
    // AND if text is opted in, phone number must be present
    if (emailOptIn && textOptIn) {
      const emailValid = emailTermsChecked;
      const textValid = textTermsChecked && textHasPhoneNumber;
      return !(emailValid || textValid);
    }
    // Otherwise enable
    return false;
  };

  return (
    <div className="row">
      <div className="col-12 mt-n2" aria-live="polite">
        {/* Page Title */}
        {FormTitles[page] !== '' && (
          <div className="row">
            <span className="visually-hidden me-1">
              {t('common.registrationCurrentPage')} {[page + 1]}
            </span>

            <Headings
              level={1}
              text={t(FormTitleTranslationKeys[FormTitles[page]])}
              variant="semiBold"
              dataTestId="registrationHeading"
              className="mb-4"
            />
          </div>
        )}

        {/* Loader */}
        {loading && <Loader />}

        {/* Email section */}
        <Paragraph level={5} text={t('common.email')} variant="medium" className="mb-4" />
        <PaperlessCommunications
          showPreferences={true}
          email={emailID}
          setState={setProfileState}
          state={profileState}
          onClickCancel={() => {
            setIsEmailModal(false);
          }}
          handleSuccess={handleSuccess}
          onConsentChange={(optIn, termsChecked) => {
            setEmailOptIn(optIn);
            setEmailTermsChecked(termsChecked);
          }}
        />

        <hr className="my-md-4 mt-0 mb-4" />
        <Paragraph
          level={5}
          text={t('proxyApproval.mobile')}
          variant="medium"
          className="mb-3"
        />
        <TextNotificationWrapper
          showPreferences={true}
          smsConsent={profileState?.smsConsent}
          setState={setProfileState}
          state={profileState}
          showPrimaryNumberProp={true}
          isCommunicationChannels={true}
          currentTextState={isText}
          onSubmitSuccess={() => {
            setSuccess(true);
          }}
          onConsentChange={(
            optIn,
            termsChecked,
            hasPhoneNumber,
            phoneNumber,
            phoneType
          ) => {
            setTextOptIn(optIn);
            setTextTermsChecked(termsChecked);
            setTextHasPhoneNumber(hasPhoneNumber || false);
            setTextPhoneNumber(phoneNumber || '');
            setTextPhoneType(phoneType || 'mobile');
          }}
        />
        {/* Main action buttons */}
        <hr className="my-md-3 mt-1 mb-2" />
        <div className="row">
          <div className="col-sm-auto col-12">
            <Button
              dataTestId={'saveButton'}
              variant="primary_btn"
              onClick={() => onSubmit()}
              type="submit"
              className="my-4 btn-md-width"
              disabled={isContinueDisabled()}
            >
              {t('common.continue')}
            </Button>
          </div>
          {getFeature?.RegistrationMPCSkip ? (
            <div className="col-sm-auto col-12">
              <Button
                dataTestId={'saveButton'}
                variant="secondary_btn"
                onClick={cancelButton}
                type="button"
                className="my-sm-4 mb-4 btn-md-width"
              >
                {t('common.skipContent')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      {/* modal for valid text, email and success messages */}
      {isTextModal && (
        <ModalOverlay
          isOpen={isTextModal}
          onClose={() => {
            setIsTextModal(false);
          }}
          overlayInfo="Modal information"
        >
          <div data-testid="amSurePopUp">
            <Headings
              level={2}
              text={t('signupNotifications')}
              variant="regular"
            />
            <Paragraph
              text={t('profile.textNotificationsDesc')}
              variant="regular"
              data-testId="iamSureContent"
              className="mb-4 pb-2"
            />
            <div className="row d-flex justify-content-start align-items-center py-2">
              <div className="col-sm-auto col-12 mb-sm-0 mb-4">
                <Button
                  dataTestId={'imSureButton'}
                  variant="primary_btn"
                  type="submit"
                  onClick={() => {
                    setIsTextModal(false);
                    setIsText(true);
                  }}
                >
                  {t('signUp')}
                </Button>
              </div>
              <div className="col-sm-auto col-12 mb-sm-0">
                <Button
                  dataTestId={'cancelButton'}
                  variant="secondary_btn"
                  onClick={() => {
                    setIsTextModal(false);
                    setIsText(false);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </div>
        </ModalOverlay>
      )}
      {isEmailModal && (
        <ModalOverlay
          isOpen={isEmailModal}
          onClose={() => {
            setIsEmailModal(false);
          }}
          overlayInfo="Modal information"
        >
          <div data-testid="amSurePopUp">
            <Headings
              level={2}
              text={t('emailconsentRequired')}
              variant="regular"
            />
            <Paragraph
              text={t('emailconsentRequiredDesc')}
              variant="regular"
              data-testId="iamSureContent"
              className="mb-4 pb-2"
            />
            <div className="d-flex flex-lg-row flex-md-row flex-column gap-lg-3 gap-md-3 gap-4 mb-2">
              <Button
                dataTestId={'imSureButton'}
                variant="primary_btn"
                type="submit"
                onClick={() => {
                  setIsEmailModal(false);
                }}
              >
                {t('signUp')}
              </Button>
              <Button
                dataTestId={'cancelButton'}
                variant="secondary_btn"
                onClick={() => {
                  setIsEmailModal(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

export default CommunicationPreferences;